<script lang="ts">
  import { mdiChevronLeft, mdiChevronRight } from "@mdi/js"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import type { PublicAppointmentSlotCalendarDay } from "../../../../../../../../../../api/public/appointment-portal"
  import {
    calendarDayHasDeadlineWarning,
    formatDate,
    formatDayNumber,
    formatWeekdayLong,
    formatWeekdayShort,
  } from "../../../../_shared/_logic/appointment-portal"

  const loadingDayPlaceholders = Array.from({ length: 7 }, (_, index) => index)

  let {
    days,
    selectedDate,
    loading = false,
    weekLabel,
    canPrevious = false,
    canNext = false,
    onprevious,
    onnext,
    onselect,
  }: {
    days: PublicAppointmentSlotCalendarDay[]
    selectedDate: string | null
    loading?: boolean
    weekLabel: string
    canPrevious?: boolean
    canNext?: boolean
    onprevious: () => void
    onnext: () => void
    onselect: (date: string) => void
  } = $props()

  let initialLoading = $derived(loading && days.length === 0)
</script>

<section class="date-selector" aria-label="Tag auswählen">
  <div class="date-selector-header">
    {#if initialLoading}
      <p class="calendar-loading-label" role="status" aria-live="polite"> Tage werden geladen </p>
    {:else}
      <div class="week-nav" aria-label="Woche wechseln">
        <button
          type="button"
          class="week-nav-button"
          aria-label="Vorherige Woche"
          disabled={loading || !canPrevious}
          onclick={onprevious}
        >
          <Icon path={mdiChevronLeft} />
        </button>
        <strong aria-live="polite" aria-atomic="true">{weekLabel}</strong>
        <button
          type="button"
          class="week-nav-button"
          aria-label="Nächste Woche"
          disabled={loading || !canNext}
          onclick={onnext}
        >
          <Icon path={mdiChevronRight} />
        </button>
      </div>
    {/if}
  </div>

  <div class="days-frame" aria-busy={loading}>
    <div class="days-grid-area">
      {#if initialLoading}
        <div class="days days--loading" aria-hidden="true">
          {#each loadingDayPlaceholders as placeholder}
            <span
              class="day-placeholder"
              data-testid="calendar-day-placeholder"
              style={`--placeholder-delay: ${placeholder * 55}ms`}
            ></span>
          {/each}
        </div>
      {:else}
        {#if loading}
          <p class="calendar-refresh-label" role="status" aria-live="polite">
            Tage werden geladen
          </p>
        {/if}
        <div class="days" class:refreshing={loading}>
          {#each days as day (day.date)}
            {@const selected = selectedDate === day.date}
            {@const deadlineWarning = day.available && calendarDayHasDeadlineWarning(day)}
            <button
              type="button"
              class:active={selected}
              class:unavailable={!day.available}
              class:deadline-warning={deadlineWarning}
              disabled={loading || !day.available}
              aria-pressed={selected}
              aria-label={`${formatDate(day.date)}${deadlineWarning ? ", Termine nach Frist" : ""}`}
              onclick={() => {
                if (!loading && day.available) onselect(day.date)
              }}
            >
              {#if selected}
                <span class="day-selected-badge" aria-hidden="true">Ausgewählt</span>
              {/if}
              <span class="day-weekday day-weekday--long" aria-hidden="true">
                {formatWeekdayLong(day.date)}
              </span>
              <span class="day-weekday day-weekday--short" aria-hidden="true">
                {formatWeekdayShort(day.date)}
              </span>
              <strong aria-hidden="true">{formatDayNumber(day.date)}</strong>
              {#if deadlineWarning}
                <span class="deadline-dot" aria-hidden="true"></span>
              {/if}
            </button>
          {/each}
        </div>
      {/if}
    </div>
  </div>
</section>

<style>
  .date-selector {
    display: grid;
    gap: 0.9rem;
    min-width: 0;
    color: var(--portal-ink, var(--bg-100));
  }

  .date-selector-header {
    display: grid;
    place-items: center;
    min-height: 2.1rem;
  }

  .week-nav {
    width: 100%;
    display: grid;
    grid-template-columns: 2.1rem minmax(0, 1fr) 2.1rem;
    align-items: center;
    gap: 0.75rem;
    color: var(--portal-ink, var(--bg-100));
    text-transform: uppercase;
    font-size: 0.82rem;
    font-weight: 800;
    text-align: center;
  }

  .calendar-loading-label,
  .calendar-refresh-label {
    margin: 0;
    color: var(--portal-muted, #697571);
    font-size: 0.78rem;
    font-weight: 800;
    text-align: center;
    text-transform: uppercase;
  }

  .calendar-refresh-label {
    margin-bottom: 0.5rem;
  }

  .week-nav-button {
    width: 2.1rem;
    height: 2.1rem;
    min-height: 0;
    display: grid;
    place-items: center;
    padding: 0;
    border: 1px solid var(--portal-line, #d6ddd8);
    background: #fff;
    color: var(--portal-ink, var(--bg-100));
  }

  .week-nav-button:disabled {
    color: #9aa5a1;
    background: var(--portal-subtle, #f7faf8);
    cursor: not-allowed;
  }

  .week-nav-button:focus-visible,
  .days button:focus-visible {
    outline: 3px solid color-mix(in srgb, var(--primary-200) 70%, #fff);
    outline-offset: 2px;
  }

  .days-frame {
    position: relative;
    min-width: 0;
  }

  .days-grid-area {
    position: relative;
    min-width: 0;
  }

  .days {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 0.65rem;
  }

  .days.refreshing {
    opacity: 0.55;
  }

  .day-placeholder {
    position: relative;
    min-height: 6.4rem;
    overflow: hidden;
    border: 1px solid var(--portal-line, #d6ddd8);
    background: var(--portal-subtle, #f7faf8);
  }

  .day-placeholder::before,
  .day-placeholder::after {
    content: "";
    position: absolute;
    left: 50%;
    border-radius: 999px;
    background: color-mix(in srgb, var(--portal-muted, #697571) 17%, transparent);
    transform: translateX(-50%);
    animation: placeholder-pulse 1.2s ease-in-out infinite alternate;
    animation-delay: var(--placeholder-delay);
  }

  .day-placeholder::before {
    top: 2.15rem;
    width: 55%;
    height: 0.45rem;
  }

  .day-placeholder::after {
    top: 3.15rem;
    width: 1.4rem;
    height: 1.2rem;
  }

  .days button {
    position: relative;
    width: 100%;
    min-width: 0;
    min-height: 6.4rem;
    display: grid;
    align-content: center;
    justify-items: center;
    gap: 0.35rem;
    border: 1px solid var(--portal-line, #d6ddd8);
    background: #fff;
    color: var(--portal-ink, var(--bg-100));
    cursor: pointer;
    font: inherit;
    text-align: center;
    padding: 1.45rem 0.55rem 0.9rem;
  }

  .days button:hover:not(:disabled),
  .days button.active {
    border-color: var(--primary-200);
    background: color-mix(in srgb, var(--primary-200) 16%, #fff);
  }

  .days button.unavailable {
    color: #8a9692;
    background: var(--portal-subtle, #f7faf8);
    cursor: not-allowed;
  }

  .days button.deadline-warning {
    border-color: #dc2626;
    background: #fff7f7;
  }

  .days button.deadline-warning:hover:not(:disabled),
  .days button.deadline-warning.active {
    border-color: #b91c1c;
    background: #fee2e2;
  }

  .deadline-dot {
    width: 0.42rem;
    height: 0.42rem;
    border-radius: 999px;
    background: #dc2626;
  }

  .day-selected-badge {
    position: absolute;
    top: 0;
    left: 0;
    padding: 0.18rem 0.36rem;
    background: var(--primary-200);
    color: var(--text-invers-100, #fff);
    font-size: 0.58rem;
    line-height: 1;
    text-transform: uppercase;
    font-weight: 900;
    letter-spacing: 0;
  }

  .day-weekday {
    max-width: 100%;
    color: var(--portal-muted, #697571);
    font-size: 0.72rem;
    line-height: 1;
    font-weight: 800;
    text-transform: uppercase;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .day-weekday--short {
    display: none;
  }

  .days button strong {
    font-size: 1.4rem;
    line-height: 1;
  }

  @keyframes placeholder-pulse {
    from {
      opacity: 0.42;
    }
    to {
      opacity: 0.9;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .day-placeholder::before,
    .day-placeholder::after {
      animation: none;
    }
  }

  @media (max-width: 700px) {
    .days {
      gap: 0.28rem;
    }

    .days button {
      min-height: 4.4rem;
      padding: 1.05rem 0.15rem 0.6rem;
    }

    .day-placeholder {
      min-height: 4.4rem;
    }

    .day-placeholder::before {
      top: 1.25rem;
    }

    .day-placeholder::after {
      top: 2.15rem;
      width: 1rem;
      height: 0.9rem;
    }

    .day-weekday--long {
      display: none;
    }

    .day-weekday--short {
      display: block;
    }

    .days button strong {
      font-size: 1rem;
    }
  }
</style>
